// Admin security API — typed wrappers around /admin/v1/security/*.

import type { ApiClient } from '../api-client.js';

export interface SecurityEvent {
  id: string;
  tenantId: string;
  tenantName: string;
  action: string;
  target: string | null;
  meta: Record<string, unknown>;
  ip: string | null;
  userAgent: string | null;
  ts: string;
}

export interface RateLimitSummary {
  available: boolean;
  layers: {
    layer: 'edge' | 'tenant' | 'key' | 'session';
    hits: number;
    available: boolean;
  }[];
  note: string;
}

export interface ListEventsParams {
  tenantId?: string;
  sinceHours?: number;
  limit?: number;
}

export const SECURITY_INJECTION_QUERY_KEY = ['admin', 'security', 'injection'] as const;
export const SECURITY_CANARY_QUERY_KEY = ['admin', 'security', 'canary'] as const;
export const SECURITY_SSRF_QUERY_KEY = ['admin', 'security', 'ssrf'] as const;
export const SECURITY_RATE_LIMITS_QUERY_KEY = ['admin', 'security', 'rate-limits'] as const;

function buildQuery(p: ListEventsParams): string {
  const sp = new URLSearchParams();
  if (p.tenantId) sp.set('tenantId', p.tenantId);
  if (p.sinceHours !== undefined) sp.set('sinceHours', String(p.sinceHours));
  if (p.limit !== undefined) sp.set('limit', String(p.limit));
  const tail = sp.toString();
  return tail.length > 0 ? `?${tail}` : '';
}

export const securityApi = {
  async injection(client: ApiClient, params: ListEventsParams = {}): Promise<{ rows: SecurityEvent[] }> {
    return client.get<{ rows: SecurityEvent[] }>(`/security/injection${buildQuery(params)}`);
  },
  async canary(client: ApiClient, params: ListEventsParams = {}): Promise<{ rows: SecurityEvent[] }> {
    return client.get<{ rows: SecurityEvent[] }>(`/security/canary${buildQuery(params)}`);
  },
  async ssrf(client: ApiClient, params: ListEventsParams = {}): Promise<{ rows: SecurityEvent[] }> {
    return client.get<{ rows: SecurityEvent[] }>(`/security/ssrf${buildQuery(params)}`);
  },
  async rateLimits(client: ApiClient): Promise<RateLimitSummary> {
    return client.get<RateLimitSummary>('/security/rate-limits');
  },
  async revokeApiKey(
    client: ApiClient,
    apiKeyId: string,
  ): Promise<{ id: string; tenantId: string; prefix: string }> {
    return client.post(`/security/apikeys/${apiKeyId}/revoke`, {});
  },
  async revokeWidgetSession(
    client: ApiClient,
    sessionId: string,
  ): Promise<{ id: string; tenantId: string }> {
    return client.post(`/security/sessions/${sessionId}/revoke`, {});
  },
  async markFalsePositive(
    client: ApiClient,
    eventId: string,
    reason?: string,
  ): Promise<{ ok: true }> {
    return client.post(
      `/security/events/${eventId}/mark-false-positive`,
      reason ? { reason } : {},
    );
  },
};

// Pull a friendly risk-level out of a meta payload from
// `prompt_injection_flagged`. Defensive — the action shape is recent
// and might shift; default to 'unknown' when absent.
export function extractRiskLevel(meta: Record<string, unknown>): string {
  const r = meta['riskLevel'];
  return typeof r === 'string' ? r : 'unknown';
}

export function riskBadgeVariant(
  risk: string,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (risk) {
    case 'high':
      return 'destructive';
    case 'medium':
      return 'default';
    case 'low':
      return 'secondary';
    default:
      return 'outline';
  }
}
